<script setup>
import { useLayout } from '@/layout/common/composables/layout';
import { useDirection } from '@/composables/useDirection';
import { cva } from 'class-variance-authority';
import { computed, inject, onMounted, ref, watch } from 'vue';

const props = defineProps({
    class: {
        type: String,
        default: ''
    },
    mode: {
        type: String,
        default: null
    }
});

const { calculatedMenuMode } = useLayout();
const { isRtl } = useDirection();
const sidebarMenuRef = ref(null);
const menuHeight = ref('auto');

const calculatedVariant = computed(() => {
    if (props.mode) {
        return props.mode;
    }
    return calculatedMenuMode.value;
});

const menuContext = inject('menuContext', null);

const updateHeight = () => {
    if (!menuContext?.collapsible) return;

    if (menuContext.isOpen.value) {
        menuHeight.value = `${sidebarMenuRef.value.scrollHeight + 4}px`;
    } else {
        menuHeight.value = '0px';
    }
};

const menuStyle = computed(() => {
    if (!menuContext?.collapsible || calculatedVariant.value === 'slim' || calculatedVariant.value === 'compact') return {};

    return {
        maxHeight: menuHeight.value,
        overflow: 'hidden',
        transition: 'max-height 0.2s ease-in-out, opacity 0.25s ease-in-out, transform 0.25s ease-in-out',
        opacity: menuContext?.isOpen.value ? 1 : 0,
        transform: menuContext?.isOpen.value ? 'translateY(0)' : 'translateY(-10px)'
    };
});

onMounted(() => {
    if (menuContext?.collapsible && calculatedVariant.value !== 'slim' && calculatedVariant.value !== 'compact') {
        updateHeight();

        // Watch for content size changes (e.g. 3rd level sub-menus toggling)
        if (sidebarMenuRef.value) {
            const observer = new MutationObserver(() => {
                if (menuContext.isOpen.value) {
                    menuHeight.value = `${sidebarMenuRef.value.scrollHeight + 4}px`;
                }
            });
            observer.observe(sidebarMenuRef.value, { childList: true, subtree: true });
        }
    }
});

watch(
    () => menuContext?.isOpen.value,
    () => {
        if (menuContext?.collapsible && calculatedVariant.value !== 'slim' && calculatedVariant.value !== 'compact') {
            updateHeight();
        }
    }
);

const sidebarMenuVariants = cva('gap-1 flex flex-col transition-all', {
    variants: {
        variant: {
            horizontal: 'flex-row'
        }
    }
});

const sidebarMenuClass = computed(() => {
    const baseClass = sidebarMenuVariants({ variant: calculatedVariant.value, class: props.class });
    // Add RTL-aware padding for nested menus
    const paddingClass = menuContext?.collapsible 
        ? (isRtl.value ? 'group-has-[[data-sidebar=menu]]/menu-item:pr-6' : 'group-has-[[data-sidebar=menu]]/menu-item:pl-6')
        : '';
    return `${baseClass} ${paddingClass}`;
});
</script>

<template>
    <ul ref="sidebarMenuRef" data-sidebar="menu" :style="menuStyle" :class="sidebarMenuClass">
        <template v-if="menuContext?.collapsible">
            <slot />
        </template>
        <template v-else>
            <slot />
        </template>
    </ul>
</template>
